import { forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { IoClose } from 'react-icons/io5'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { arSA, enUS } from 'date-fns/locale'
import { cloneDeep, toUpper, trim, filter, forEach, fromPairs, get, includes, isEmpty, map, pick, range, sortBy, uniq } from 'lodash'
import apiPut from 'lib/network/apiPut'
import notify from 'components/atoms/Notify'
import Switch from 'components/atoms/Switch'
import { Checkbox, Field, Notice, NumberInput, Panel, Select, btn, fmtDate, inputClass, isBlank, toNumber } from './refundShared'

registerLocale('ar', arSA)
registerLocale('en', enUS)

/** The field of the holidays picker: shows the selected dates inside the input, with a clear button. */
const HolidaysField = forwardRef(({ holidays, placeholder, onClear, clearLabel, onClick }, ref) => (
  <div className={`${inputClass()} flex items-center gap-2 cursor-pointer min-h-[2.5rem]`} onClick={onClick} ref={ref} role="button" tabIndex={0}>
    <span className={`flex-1 truncate ${holidays.length ? 'text-gray-900' : 'text-gray-400'}`} title={holidays.join(', ')}>
      {holidays.length ? map(holidays, day => moment(day, 'YYYY-MM-DD').format('DD MMM YYYY')).join(', ') : placeholder}
    </span>
    {holidays.length > 0 && (
      <button
        type="button"
        aria-label={clearLabel}
        onClick={e => {
          e.stopPropagation()
          onClear()
        }}
        className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
      >
        <IoClose className="h-4 w-4" />
      </button>
    )}
  </div>
))
HolidaysField.displayName = 'HolidaysField'

const FX_BASES = ['paidAmount', 'cardPaidAmount', 'refundAmount']
const NUMBER_FIELDS = [
  { name: 'fxFeePercent', min: 0, max: 100, addon: '%' },
  { name: 'bankFeeFixed', min: 0, addon: 'OMR' },
  { name: 'processingSlaDays', min: 0 },
  { name: 'bankTransferSlaDays', min: 0 },
  { name: 'refundValidityMonths', min: 1 },
  { name: 'customerCancelMinHours', min: 0 },
]
const PLAIN_FIELDS = ['timezone', 'workingDays', 'fxFeeBase', 'cancellationReasons', ...map(NUMBER_FIELDS, 'name')]
const REASON_FIELDS = ['code', 'en', 'ar', 'audience']

const RefundSettings = ({ settings, onSaved }) => {
  const { t, i18n } = useTranslation()
  const [values, setValues] = useState(() => ({
    ...cloneDeep(pick(settings, PLAIN_FIELDS)),
    workingFrom: get(settings, 'workingHours.from', '10:00'),
    workingTo: get(settings, 'workingHours.to', '18:00'),
  }))
  const [holidays, setHolidays] = useState(get(settings, 'holidays', []))
  const toggleHoliday = day => setHolidays(includes(holidays, day) ? filter(holidays, d => d !== day) : sortBy(uniq([...holidays, day])))
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  if (!settings) return null

  const required = t('refundEngine.common.required')
  const setValue = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }))
    setErrors(prev => ({ ...prev, [key]: undefined }))
  }
  const reasons = values.cancellationReasons || []
  const setReason = (index, key, value) => {
    setValue(
      'cancellationReasons',
      map(reasons, (reason, i) => (i === index ? { ...reason, [key]: value } : reason))
    )
    setErrors(prev => ({ ...prev, [`reason.${index}.${key}`]: undefined }))
  }

  const validate = () => {
    const next = {}
    forEach(['timezone', 'workingFrom', 'workingTo', ...map(NUMBER_FIELDS, 'name')], key => {
      if (isBlank(values[key])) next[key] = required
    })
    if (toNumber(values.fxFeePercent) > 0 && !values.fxFeeBase) next.fxFeeBase = t('refundEngine.settings.fxFeeBaseRequired')
    forEach(reasons, (reason, index) => {
      forEach(REASON_FIELDS, key => {
        if (isBlank(reason[key])) next[`reason.${index}.${key}`] = required
      })
    })
    setErrors(next)
    return isEmpty(next)
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (!validate()) return
    const body = {
      ...pick(values, PLAIN_FIELDS),
      ...fromPairs(map(NUMBER_FIELDS, field => [field.name, toNumber(values[field.name])])),
      fxFeeBase: values.fxFeeBase || null,
      workingHours: { from: values.workingFrom, to: values.workingTo },
      holidays,
      // Only the fields the API accepts, so stray fields on stored reasons can't block a save.
      cancellationReasons: map(reasons, reason => ({
        code: toUpper(trim(reason.code)),
        en: trim(reason.en),
        ar: trim(reason.ar),
        audience: reason.audience || 'customer',
        isActive: reason.isActive !== false,
      })),
    }
    setSaving(true)
    const response = await apiPut('refund-settings', body)
    setSaving(false)
    if (response?.success) {
      notify.success(t('refundEngine.settings.saved', { version: get(response, 'data.version') }))
      onSaved(response.data)
    }
  }

  const workingDays = values.workingDays || []

  return (
    <form onSubmit={handleSubmit} noValidate className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-6xl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <span className="text-sm text-gray-600">
          {t('refundEngine.settings.currentVersion')}: <span className="font-semibold text-gray-900">v{settings.version}</span> · {fmtDate(settings.createdAt)}
        </span>
        <button type="submit" className={btn('primary')} disabled={saving}>
          {saving ? t('common.saving') : t('refundEngine.settings.saveNewVersion')}
        </button>
      </div>

      <Panel>
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label={t('refundEngine.settings.timezone')} htmlFor="set-timezone" error={errors.timezone}>
              <input id="set-timezone" value={values.timezone || ''} onChange={e => setValue('timezone', e.target.value)} className={inputClass(errors.timezone)} />
            </Field>
            <Field label={t('refundEngine.settings.workingFrom')} htmlFor="set-from" error={errors.workingFrom}>
              <input
                id="set-from"
                type="time"
                value={values.workingFrom || ''}
                onChange={e => setValue('workingFrom', e.target.value)}
                className={inputClass(errors.workingFrom)}
              />
            </Field>
            <Field label={t('refundEngine.settings.workingTo')} htmlFor="set-to" error={errors.workingTo}>
              <input id="set-to" type="time" value={values.workingTo || ''} onChange={e => setValue('workingTo', e.target.value)} className={inputClass(errors.workingTo)} />
            </Field>
          </div>

          <div>
            <p className="block text-sm font-medium text-gray-700 mb-2">{t('refundEngine.settings.workingDays')}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {map(range(7), day => (
                <Checkbox
                  key={day}
                  checked={includes(workingDays, day)}
                  onChange={checked => setValue('workingDays', checked ? sortBy(uniq([...workingDays, day])) : filter(workingDays, d => d !== day))}
                >
                  {moment.weekdays(day)}
                </Checkbox>
              ))}
            </div>
          </div>

          <Field label={t('refundEngine.settings.holidays')} htmlFor="set-holiday" hint={t('refundEngine.settings.holidaysHint')}>
            {/* Multi-date picker: stays open, click a date to add it, click a highlighted date to remove it. */}
            <DatePicker
              id="set-holiday"
              selected={null}
              onChange={date => date && toggleHoliday(moment(date).format('YYYY-MM-DD'))}
              highlightDates={map(holidays, day => moment(day, 'YYYY-MM-DD').toDate())}
              shouldCloseOnSelect={false}
              locale={i18n.language === 'ar' ? 'ar' : 'en'}
              customInput={
                <HolidaysField
                  holidays={holidays}
                  placeholder={t('refundEngine.settings.addHoliday')}
                  onClear={() => setHolidays([])}
                  clearLabel={t('refundEngine.common.remove')}
                />
              }
            />
          </Field>
        </div>
      </Panel>

      <Panel title={t('refundEngine.settings.feesAndLimits')}>
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {map(NUMBER_FIELDS, field => (
              <Field key={field.name} label={t(`refundEngine.settings.${field.name}`)} htmlFor={`set-${field.name}`} error={errors[field.name]}>
                <NumberInput
                  id={`set-${field.name}`}
                  min={field.min}
                  max={field.max}
                  addon={field.addon}
                  value={values[field.name]}
                  onChange={v => {
                    setValue(field.name, v)
                    if (field.name === 'fxFeePercent') setErrors(prev => ({ ...prev, fxFeeBase: undefined }))
                  }}
                  error={errors[field.name]}
                />
              </Field>
            ))}
            <Field label={t('refundEngine.settings.fxFeeBase')} error={errors.fxFeeBase}>
              <Select
                clearable
                placeholder={t('refundEngine.settings.fxFeeBasePlaceholder')}
                value={values.fxFeeBase || undefined}
                onChange={v => setValue('fxFeeBase', v)}
                options={map(FX_BASES, v => ({ value: v, label: t(`refundEngine.fxBase.${v}`) }))}
                error={errors.fxFeeBase}
              />
            </Field>
          </div>
          <Notice type="info" title={t('refundEngine.settings.fxFeeBaseHint')} />
        </div>
      </Panel>

      <Panel
        title={t('refundEngine.settings.reasons')}
        right={
          <button type="button" className={btn('secondary', true)} onClick={() => setValue('cancellationReasons', [...reasons, { audience: 'customer', isActive: true }])}>
            {t('refundEngine.settings.addReason')}
          </button>
        }
        bodyClassName="p-0"
      >
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200 text-xs text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-2 font-normal text-left rtl:text-right w-40">{t('refundEngine.settings.reasonCode')}</th>
                <th className="px-4 py-2 font-normal text-left rtl:text-right">{t('refundEngine.settings.reasonEn')}</th>
                <th className="px-4 py-2 font-normal text-left rtl:text-right">{t('refundEngine.settings.reasonAr')}</th>
                <th className="px-4 py-2 font-normal text-left rtl:text-right w-40">{t('refundEngine.settings.audience')}</th>
                <th className="px-4 py-2 font-normal text-left rtl:text-right w-20">{t('refundEngine.rules.columns.active')}</th>
                <th className="px-4 py-2 w-20" aria-label={t('refundEngine.common.remove')} />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {map(reasons, (reason, index) => {
                const err = key => errors[`reason.${index}.${key}`]
                return (
                  <tr key={index} className="align-top">
                    <td className="px-4 py-2 min-w-[8rem]">
                      <input
                        placeholder="CODE"
                        aria-label="code"
                        value={reason.code || ''}
                        onChange={e => setReason(index, 'code', e.target.value)}
                        className={inputClass(err('code'))}
                      />
                      {err('code') && <p className="mt-1 text-xs text-red-600">{err('code')}</p>}
                    </td>
                    <td className="px-4 py-2 min-w-[12rem]">
                      <input aria-label="en" value={reason.en || ''} onChange={e => setReason(index, 'en', e.target.value)} className={inputClass(err('en'))} />
                      {err('en') && <p className="mt-1 text-xs text-red-600">{err('en')}</p>}
                    </td>
                    <td className="px-4 py-2 min-w-[12rem]">
                      <input aria-label="ar" dir="rtl" value={reason.ar || ''} onChange={e => setReason(index, 'ar', e.target.value)} className={inputClass(err('ar'))} />
                      {err('ar') && <p className="mt-1 text-xs text-red-600">{err('ar')}</p>}
                    </td>
                    <td className="px-4 py-2 min-w-[9rem]">
                      <select
                        aria-label="audience"
                        value={reason.audience || ''}
                        onChange={e => setReason(index, 'audience', e.target.value)}
                        className={inputClass(err('audience'))}
                      >
                        {!reason.audience && <option value="" />}
                        {map(['customer', 'ops'], v => (
                          <option key={v} value={v}>
                            {t(`refundEngine.settings.audiences.${v}`)}
                          </option>
                        ))}
                      </select>
                      {err('audience') && <p className="mt-1 text-xs text-red-600">{err('audience')}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <Switch value={Boolean(reason.isActive)} onChange={checked => setReason(index, 'isActive', checked)} />
                    </td>
                    <td className="px-4 py-3 text-right rtl:text-left">
                      <button
                        type="button"
                        className="text-sm font-medium text-red-600 hover:text-red-700"
                        onClick={() => {
                          setValue(
                            'cancellationReasons',
                            filter(reasons, (_reason, i) => i !== index)
                          )
                          setErrors({})
                        }}
                      >
                        {t('refundEngine.common.remove')}
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </form>
  )
}

export default RefundSettings
